/**
 * "You added this word to known" toast — web port of the iOS confirmation shown
 * after a word is saved to known (client-native
 * EnglishSentenceActivity.tsx `showKnownToast`: a bottom spring toast with a
 * green check-circle + "You added this word to known", auto-dismissing after a
 * couple of seconds).
 *
 * Presentational only. Drive it via the `useKnownWordToast` hook (separate file
 * so this stays component-only for Fast Refresh). The slide-in plays via a CSS
 * keyframe on mount; the hook unmounts it (message → null) after the timeout.
 */
import { createPortal } from "react-dom";
import { CheckCircle2 } from "lucide-react";
import { colors } from "@/ui";

export default function KnownWordToast({ message }: { message: string | null }) {
  if (!message) return null;

  return createPortal(
    <div
      aria-live="polite"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        // Sit ABOVE the activity's Continue button, not on top of it (iOS
        // EnglishSentenceActivity toast: insets.bottom + footer pad + button
        // height + gap). Web footer is `padding:12px 20px 20px` around a
        // `var(--button-height)` button, so clear the button height + its 20px
        // bottom pad + a 12px gap.
        bottom:
          "calc(env(safe-area-inset-bottom, 0px) + var(--button-height, 46px) + 32px)",
        display: "flex",
        justifyContent: "center",
        zIndex: 90,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          maxWidth: "calc(100% - 32px)",
          padding: "10px 16px",
          borderRadius: 999,
          background: "#fff",
          boxShadow: "0 6px 20px rgba(0,0,0,0.16)",
          border: "1px solid rgba(0,0,0,0.06)",
          animation: "known-word-toast-in 0.28s cubic-bezier(0.2,0.9,0.3,1)",
        }}
      >
        <CheckCircle2 size={20} color={colors.green} />
        <span style={{ fontSize: 15, fontWeight: 600, color: colors.primary }}>
          {message}
        </span>
      </div>
      <style>{`@keyframes known-word-toast-in {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }`}</style>
    </div>,
    document.body
  );
}
